import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Volume2, VolumeX } from 'lucide-react';
export function HeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current) {
        // Play the video after 3 seconds
        videoRef.current.play().catch(e => console.log('Autoplay prevented:', e));
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.02
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative min-h-[calc(100vh-80px)] flex items-center justify-center pt-8 md:pt-12 pb-12 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden" data-id="element-65">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-66">
        <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[50%] rounded-full bg-brand-orange/5 blur-3xl" data-id="element-67"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-gray-100 blur-3xl" data-id="element-68"></div>
      </div>

      <div className="max-w-5xl mx-auto text-center z-10 relative" data-id="element-69">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4 md:space-y-6" data-id="element-70">
          <motion.div variants={itemVariants} className="flex justify-center px-2" data-id="element-71">
            <span className="bg-brand-black text-white text-[10px] sm:text-xs md:text-sm font-bold px-4 py-2 md:px-5 md:py-2 rounded-full uppercase tracking-wider inline-block text-center shadow-sm" data-id="element-72">
              MÉTODO VSPP será un éxito para vender tu servicio.
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-brand-black leading-tight tracking-tight" data-id="element-73">
            Implementá el Método VSPP y generá al menos{' '}
            <span className="text-brand-orange" data-id="element-74">$800.000 en ventas</span> a tu
            negocio de servicios en sólo 3 meses
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-75">
            Si ya estás vendiendo servicios pero no lográs ingresos constantes,
            acá tenés un sistema claro para ordenar tu negocio, vender mejor y
            dejar de depender del azar.
          </motion.p>

          <motion.div variants={itemVariants} className="w-full max-w-3xl mx-auto mt-6 mb-4 rounded-2xl overflow-hidden shadow-2xl border-4 border-white ring-1 ring-black/5 relative group cursor-pointer" onClick={toggleMute} data-id="element-76">
            <video ref={videoRef} className="w-full aspect-video object-cover bg-brand-black" muted loop playsInline preload="auto" data-id="element-77">
              <source src="https://cdn.magicpatterns.com/uploads/jVAB8DRr3tAsow9zsY4SvM/HORIZONTAL%20VIDEO.mp4" type="video/mp4" data-id="element-78" />
              Tu navegador no soporta el elemento de video.
            </video>

            {/* Custom Unmute Overlay */}
            <div className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white px-3 py-1.5 rounded-full backdrop-blur-sm transition-all flex items-center gap-1.5" data-id="element-79">
              {isMuted ? <>
                  <VolumeX size={16} data-id="element-80" />
                  <span className="text-xs font-medium pr-0.5" data-id="element-81">
                    Tocar para escuchar
                  </span>
                </> : <Volume2 size={16} data-id="element-82" />}
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 pt-4" data-id="element-83">
            <a href="https://centrocumbre.com/cart/46319925100743:1" className="w-full sm:w-auto bg-brand-orange text-brand-black font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:scale-[1.03] transition-transform animate-pulse-subtle flex items-center justify-center" data-id="element-84">
              Quiero escalar mi negocio ahora
            </a>

            <a href="https://centrocumbre.com/cart/46319925100743:1" className="w-full sm:w-auto border-2 border-brand-orange text-brand-black font-bold text-lg px-8 py-4 rounded-xl hover:bg-brand-orange/10 transition-colors flex items-center justify-center" data-id="element-85">
              Ingresar ahora
            </a>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4" data-id="element-86">
            <a href="https://wa.me/5492617508744?text=Hola,%20quiero%20más%20información%20sobre%20el%20método%20VSPP" className="flex items-center gap-2 text-brand-black font-medium hover:text-brand-orange transition-colors" data-id="element-87">
              <MessageCircle size={20} className="text-[#25D366]" data-id="element-88" />
              Consultar por WhatsApp
            </a>

            <a href="#dudas" className="text-gray-500 underline hover:text-brand-black transition-colors" data-id="element-89">
              Sacarme dudas ahora
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}